import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Uploader from "../../../../components/images/Uploader";
import CreateSubcategoryModal from "../../../../components/modals/CreateSubcategoryModal";
import CreateCategoryModal from "../../../../components/modals/CreateCategoryModal";

import { adminListCategories } from "../../../../actions/categoryActions";
import { adminListSubcategories } from "../../../../actions/subcategoryActions";

import {
  FIELD_FLAG_LABELS,
  FLAG_FIELDS,
  GENDER_OPTIONS,
  SHOP_KEY,
  Field,
  Section,
  getIdValue,
  mergeById,
  normalizeSku,
  safeText,
} from "./ReadyMadeProductForm.shared";

const ReadyMadeCatalogSection = memo(function ReadyMadeCatalogSection({
  shopKey = SHOP_KEY,
  category,
  subcategory,
  disabled = false,
  onCategoryChange,
  onSubcategoryChange,
}) {
  const dispatch = useDispatch();

  const categoryState = useSelector((state) => state.adminCategoryList || {});
  const subcategoryState = useSelector(
    (state) => state.adminSubcategoryList || {},
  );

  const [createdCategories, setCreatedCategories] = useState([]);
  const [createdSubcategories, setCreatedSubcategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);

  const categoryList = useMemo(
    () => categoryState.categories || categoryState.items || [],
    [categoryState.categories, categoryState.items],
  );
  const subcategoryList = useMemo(
    () => subcategoryState.subcategories || subcategoryState.items || [],
    [subcategoryState.items, subcategoryState.subcategories],
  );

  const loadingCategories = Boolean(categoryState.loading);
  const loadingSubcategories = Boolean(subcategoryState.loading);

  const categoriesError = safeText(categoryState.error);
  const subcategoriesError = safeText(subcategoryState.error);

  useEffect(() => {
    if (!shopKey) return;

    dispatch(adminListCategories({ shopKey }));
    dispatch(adminListSubcategories({ shopKey }));
  }, [dispatch, shopKey]);

  useEffect(() => {
    if (!category && subcategory) {
      onSubcategoryChange("");
    }
  }, [category, subcategory, onSubcategoryChange]);

  const categories = useMemo(
    () =>
      mergeById([...categoryList, ...createdCategories]).filter((item) => {
        const itemShopKey = safeText(item?.shopKey);
        return !shopKey || !itemShopKey || itemShopKey === shopKey;
      }),
    [categoryList, createdCategories, shopKey],
  );

  const subcategories = useMemo(
    () =>
      mergeById([...subcategoryList, ...createdSubcategories]).filter(
        (item) => {
          const itemShopKey = safeText(item?.shopKey);
          const parentId = getIdValue(item?.parentId);

          const sameShop = !shopKey || !itemShopKey || itemShopKey === shopKey;
          const sameParent = !category || parentId === category;

          return sameShop && sameParent;
        },
      ),
    [category, createdSubcategories, shopKey, subcategoryList],
  );

  const handleCreatedCategory = useCallback(
    (created) => {
      const id = getIdValue(created);
      if (!id) return;

      setCreatedCategories((prev) => mergeById([...prev, created]));
      onCategoryChange(id);
      onSubcategoryChange("");
      setShowCategoryModal(false);
    },
    [onCategoryChange, onSubcategoryChange],
  );

  const handleCreatedSubcategory = useCallback(
    (created) => {
      const id = getIdValue(created);
      if (!id) return;

      const parentId = getIdValue(created?.parentId);

      setCreatedSubcategories((prev) => mergeById([...prev, created]));

      if (parentId) {
        onCategoryChange(parentId);
      }

      onSubcategoryChange(id);
      setShowSubcategoryModal(false);
    },
    [onCategoryChange, onSubcategoryChange],
  );

  return (
    <>
      <Section
        title="Category Assignment"
        hint="Choose an existing category or create one without leaving the product form."
      >
        <div className="admin-form-grid admin-form-grid--2">
          <div className="admin-field">
            <div className="admin-field-row">
              <span className="admin-field__label">Category</span>

              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowCategoryModal(true)}
                disabled={disabled || !shopKey}
              >
                + New category
              </button>
            </div>

            <select
              className="admin-select"
              value={category}
              onChange={(event) => {
                onCategoryChange(event.target.value);
                onSubcategoryChange("");
              }}
              disabled={disabled || !shopKey || loadingCategories}
            >
              <option value="">
                {!shopKey
                  ? "Choose shop first"
                  : loadingCategories
                    ? "Loading categories..."
                    : categories.length
                      ? "Select category"
                      : "No categories yet"}
              </option>

              {categories.map((item) => {
                const id = getIdValue(item);

                return (
                  <option key={id} value={id}>
                    {item.name}
                  </option>
                );
              })}
            </select>

            {categoriesError ? (
              <div className="admin-feedback admin-feedback--error">
                {categoriesError}
              </div>
            ) : null}
          </div>

          <div className="admin-field">
            <div className="admin-field-row">
              <span className="admin-field__label">Subcategory</span>

              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowSubcategoryModal(true)}
                disabled={disabled || !shopKey || !category}
              >
                + New subcategory
              </button>
            </div>

            <select
              className="admin-select"
              value={subcategory}
              onChange={(event) => onSubcategoryChange(event.target.value)}
              disabled={disabled || !category || loadingSubcategories}
            >
              <option value="">
                {!category
                  ? "Choose category first"
                  : loadingSubcategories
                    ? "Loading subcategories..."
                    : subcategories.length
                      ? "Select subcategory"
                      : "No subcategories yet"}
              </option>

              {subcategories.map((item) => {
                const id = getIdValue(item);

                return (
                  <option key={id} value={id}>
                    {item.name}
                  </option>
                );
              })}
            </select>

            {subcategoriesError ? (
              <div className="admin-feedback admin-feedback--error">
                {subcategoriesError}
              </div>
            ) : null}
          </div>
        </div>
      </Section>

      <CreateCategoryModal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        shopKey={shopKey}
        onCreated={handleCreatedCategory}
      />

      <CreateSubcategoryModal
        show={showSubcategoryModal}
        onHide={() => setShowSubcategoryModal(false)}
        shopKey={shopKey}
        parentId={category}
        categories={categories}
        onCreated={handleCreatedSubcategory}
      />
    </>
  );
});

const ReadyMadeBasicSection = memo(function ReadyMadeBasicSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Basic Information"
      hint="Name, description, category, and SKU rules must match backend validation."
    >
      <div className="admin-form-grid admin-form-grid--3">
        <Field label="Product name">
          <input
            className="admin-input"
            value={form.name}
            placeholder="Product name"
            disabled={disabled}
            onChange={(event) => setField("name", event.target.value)}
          />
        </Field>

        <Field label="Brand">
          <input
            className="admin-input"
            value={form.brand}
            placeholder="Brand"
            disabled={disabled}
            onChange={(event) => setField("brand", event.target.value)}
          />
        </Field>

        {!form.isVariant ? (
          <Field label="SKU" hint="Required for simple products.">
            <input
              className="admin-input"
              value={form.sku}
              placeholder="SKU"
              disabled={disabled}
              onChange={(event) => setField("sku", event.target.value)}
              onBlur={() => setField("sku", normalizeSku(form.sku))}
            />
          </Field>
        ) : (
          <Field label="Variant mode">
            <input
              className="admin-input"
              value="Each variant has its own SKU"
              disabled
              readOnly
            />
          </Field>
        )}
      </div>

      <div className="admin-form-spacer">
        <Field label="Description">
          <textarea
            className="admin-textarea"
            value={form.description}
            placeholder="Description"
            rows={4}
            disabled={disabled}
            onChange={(event) => setField("description", event.target.value)}
          />
        </Field>
      </div>
    </Section>
  );
});

const ReadyMadeSellingModeToggle = memo(function ReadyMadeSellingModeToggle({
  isVariant,
  disabled,
  setField,
}) {
  return (
    <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
      <label className="admin-inline-check">
        <input
          type="radio"
          name="ready-made-selling-mode"
          checked={!isVariant}
          disabled={disabled}
          onChange={() => setField("isVariant", false)}
        />
        <span>Single product</span>
      </label>

      <label className="admin-inline-check">
        <input
          type="radio"
          name="ready-made-selling-mode"
          checked={isVariant}
          disabled={disabled}
          onChange={() => setField("isVariant", true)}
        />
        <span>Product with variants</span>
      </label>
    </div>
  );
});

const ReadyMadeSimpleSellingFields = memo(
  function ReadyMadeSimpleSellingFields({ form, disabled, setField }) {
    return (
      <div className="admin-form-grid admin-form-grid--4">
        <Field label="Price">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            disabled={disabled}
            onChange={(event) => setField("price", event.target.value)}
          />
        </Field>

        <Field label="Stock">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={form.countInStock}
            disabled={disabled}
            onChange={(event) => setField("countInStock", event.target.value)}
          />
        </Field>

        <Field label="Reserved">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={form.countReserved}
            disabled={disabled}
            onChange={(event) => setField("countReserved", event.target.value)}
          />
        </Field>

        <Field label="Items per pack">
          <input
            className="admin-input"
            type="number"
            min="1"
            step="1"
            value={form.itemsPerPack}
            disabled={disabled}
            onChange={(event) => setField("itemsPerPack", event.target.value)}
          />
        </Field>

        <Field label="Unit label">
          <input
            className="admin-input"
            value={form.unitLabel}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) => setField("unitLabel", event.target.value)}
          />
        </Field>
      </div>
    );
  },
);

const ReadyMadeVariantRow = memo(function ReadyMadeVariantRow({
  variant,
  index,
  disabled,
  setVariantField,
  removeVariant,
}) {
  const handleVariantImagesChange = useCallback(
    (value) => setVariantField(index, "images", value),
    [index, setVariantField],
  );

  return (
    <div className="admin-variant-card">
      <div className="admin-form-grid admin-form-grid--4">
        <Field label="Variant name">
          <input
            className="admin-input"
            value={variant.name}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "name", event.target.value)
            }
          />
        </Field>

        <Field label="SKU">
          <input
            className="admin-input"
            value={variant.sku}
            placeholder="SKU"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "sku", event.target.value)
            }
            onBlur={() =>
              setVariantField(index, "sku", normalizeSku(variant.sku))
            }
          />
        </Field>

        <Field label="Price">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={variant.price}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "price", event.target.value)
            }
          />
        </Field>

        <Field label="Stock">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={variant.countInStock}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "countInStock", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--4 admin-form-spacer">
        <Field label="Reserved">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={variant.countReserved}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "countReserved", event.target.value)
            }
          />
        </Field>

        <Field label="Items per pack">
          <input
            className="admin-input"
            type="number"
            min="1"
            step="1"
            value={variant.itemsPerPack}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "itemsPerPack", event.target.value)
            }
          />
        </Field>

        <Field label="Unit label">
          <input
            className="admin-input"
            value={variant.unitLabel}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "unitLabel", event.target.value)
            }
          />
        </Field>

        <Field label="Length / measurement">
          <input
            className="admin-input"
            value={variant.length}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "length", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--3 admin-form-spacer">
        <Field label="Color">
          <input
            className="admin-input"
            value={variant.color}
            placeholder="Color"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "color", event.target.value)
            }
          />
        </Field>

        <Field label="Size">
          <input
            className="admin-input"
            value={variant.size}
            placeholder="Size"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "size", event.target.value)
            }
          />
        </Field>

        <Field label="Material">
          <input
            className="admin-input"
            value={variant.material}
            placeholder="Material"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "material", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-form-spacer">
        <Field
          label="Short variant description"
          hint="Optional. Describe only what is distinctive about this variant."
        >
          <textarea
            className="admin-textarea"
            value={variant.shortDescription}
            placeholder="Optional"
            maxLength={240}
            rows={3}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "shortDescription", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-field admin-form-spacer">
        <span className="admin-field__label">Variant images</span>

        <Uploader
          existingImages={variant.images || []}
          setImageUrls={handleVariantImagesChange}
          folder="products"
          disabled={disabled}
        />

        <span className="admin-field__hint">
          Variant images override product-level imagery where appropriate.
        </span>
      </div>

      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-btn admin-btn--secondary"
          onClick={() => removeVariant(index)}
          disabled={disabled}
        >
          Remove variant
        </button>
      </div>
    </div>
  );
});

const ReadyMadeVariantSellingFields = memo(
  function ReadyMadeVariantSellingFields({
    form,
    disabled,
    setVariantField,
    addVariant,
    removeVariant,
  }) {
    return (
      <>
        <p className="admin-form-section__hint admin-form-spacer">
          Each variant needs its own SKU, price, stock, and optional
          distinguishing attributes.
        </p>

        {form.variants.map((variant, index) => (
          <ReadyMadeVariantRow
            key={variant._clientId || `ready-made-variant-${index}`}
            variant={variant}
            index={index}
            disabled={disabled}
            setVariantField={setVariantField}
            removeVariant={removeVariant}
          />
        ))}

        <div className="admin-form-actions">
          <button
            type="button"
            className="admin-btn admin-btn--secondary"
            onClick={addVariant}
            disabled={disabled}
          >
            Add variant
          </button>
        </div>
      </>
    );
  },
);

const ReadyMadeSellingSection = memo(function ReadyMadeSellingSection({
  form,
  disabled,
  setField,
  setVariantField,
  addVariant,
  removeVariant,
}) {
  return (
    <Section
      title="Selling Setup"
      hint="Simple products use one root SKU. Variant products require one SKU per variant."
    >
      <ReadyMadeSellingModeToggle
        isVariant={form.isVariant}
        disabled={disabled}
        setField={setField}
      />

      {!form.isVariant ? (
        <ReadyMadeSimpleSellingFields
          form={form}
          disabled={disabled}
          setField={setField}
        />
      ) : (
        <ReadyMadeVariantSellingFields
          form={form}
          disabled={disabled}
          setVariantField={setVariantField}
          addVariant={addVariant}
          removeVariant={removeVariant}
        />
      )}
    </Section>
  );
});

const ReadyMadeDetailsSection = memo(function ReadyMadeDetailsSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Ready-Made Details"
      hint="These fields power filtering, merchandising, and public product discovery."
    >
      <div className="admin-form-grid admin-form-grid--3">
        <Field label="Gender">
          <select
            className="admin-select"
            value={form.gender}
            disabled={disabled}
            onChange={(event) => setField("gender", event.target.value)}
          >
            {GENDER_OPTIONS.map((option) => (
              <option key={option.value || "empty"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Era">
          <input
            className="admin-input"
            value={form.era}
            placeholder="Era"
            disabled={disabled}
            onChange={(event) => setField("era", event.target.value)}
          />
        </Field>

        <Field label="Fit">
          <input
            className="admin-input"
            value={form.fit}
            placeholder="Fit"
            disabled={disabled}
            onChange={(event) => setField("fit", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Silhouette">
          <input
            className="admin-input"
            value={form.silhouette}
            placeholder="Silhouette"
            disabled={disabled}
            onChange={(event) => setField("silhouette", event.target.value)}
          />
        </Field>

        <Field label="Closure">
          <input
            className="admin-input"
            value={form.closure}
            placeholder="Closure"
            disabled={disabled}
            onChange={(event) => setField("closure", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--3 admin-form-spacer">
        <Field label="Colors" hint="Comma or new-line separated.">
          <textarea
            className="admin-textarea"
            value={form.colorsText}
            rows={3}
            disabled={disabled}
            placeholder="Black, Cream"
            onChange={(event) => setField("colorsText", event.target.value)}
          />
        </Field>

        <Field label="Sizes" hint="Comma or new-line separated.">
          <textarea
            className="admin-textarea"
            value={form.sizesText}
            rows={3}
            disabled={disabled}
            placeholder="S, M, L"
            onChange={(event) => setField("sizesText", event.target.value)}
          />
        </Field>

        <Field label="Materials" hint="Comma or new-line separated.">
          <textarea
            className="admin-textarea"
            value={form.materialsText}
            rows={3}
            disabled={disabled}
            placeholder="Cotton, Silk"
            onChange={(event) => setField("materialsText", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Patterns">
          <textarea
            className="admin-textarea"
            value={form.patternsText}
            rows={2}
            disabled={disabled}
            placeholder="Floral, Plain"
            onChange={(event) => setField("patternsText", event.target.value)}
          />
        </Field>

        <Field label="Tags">
          <textarea
            className="admin-textarea"
            value={form.tagsText}
            rows={2}
            disabled={disabled}
            placeholder="occasionwear, summer"
            onChange={(event) => setField("tagsText", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Groups">
          <textarea
            className="admin-textarea"
            value={form.groupsText}
            rows={2}
            disabled={disabled}
            placeholder="bridal, editorial"
            onChange={(event) => setField("groupsText", event.target.value)}
          />
        </Field>

        <Field label="Origin">
          <input
            className="admin-input"
            value={form.origin}
            placeholder="Origin"
            disabled={disabled}
            onChange={(event) => setField("origin", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Usage">
          <input
            className="admin-input"
            value={form.usage}
            placeholder="Usage"
            disabled={disabled}
            onChange={(event) => setField("usage", event.target.value)}
          />
        </Field>

        <Field label="Care instructions">
          <textarea
            className="admin-textarea"
            value={form.careInstructions}
            rows={3}
            disabled={disabled}
            placeholder="Care instructions"
            onChange={(event) =>
              setField("careInstructions", event.target.value)
            }
          />
        </Field>
      </div>
    </Section>
  );
});

const ReadyMadeImagesSection = memo(function ReadyMadeImagesSection({
  images,
  disabled,
  setImages,
}) {
  return (
    <Section
      title="Product Images"
      hint="The first image is used as the cover image."
    >
      <Uploader
        existingImages={images}
        setImageUrls={setImages}
        folder="products"
        disabled={disabled}
      />
    </Section>
  );
});

const ReadyMadeMadeToMeasureSection = memo(function ReadyMadeMadeToMeasureSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Made To Measure"
      hint="Only staff-approved designs appear in the guided made-to-measure garment step."
    >
      <label className="admin-inline-check">
        <input
          type="checkbox"
          checked={Boolean(form.madeToMeasureEligible)}
          disabled={disabled}
          onChange={(event) =>
            setField("madeToMeasureEligible", event.target.checked)
          }
        />
        <span>Available as a made-to-measure design</span>
      </label>

      <div className="admin-form-spacer">
        <Field label="Suitability notes">
          <textarea
            className="admin-textarea"
            value={form.madeToMeasureSuitabilityNotes}
            rows={3}
            disabled={disabled}
            placeholder="Fit, fabric, or construction notes for the MTM team."
            onChange={(event) =>
              setField("madeToMeasureSuitabilityNotes", event.target.value)
            }
          />
        </Field>
      </div>
    </Section>
  );
});

const ReadyMadeFlagsSection = memo(function ReadyMadeFlagsSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Visibility & Flags"
      hint="These content flags are editable on create and edit. Publishing and draft state are handled from the product list."
    >
      <div className="admin-flags-grid">
        {FLAG_FIELDS.map((key) => (
          <label key={key} className="admin-inline-check">
            <input
              type="checkbox"
              checked={Boolean(form[key])}
              disabled={disabled}
              onChange={(event) => setField(key, event.target.checked)}
            />
            <span>{FIELD_FLAG_LABELS[key] || key}</span>
          </label>
        ))}
      </div>
    </Section>
  );
});

const ReadyMadeFormContent = memo(function ReadyMadeFormContent({
  form,
  shopKey,
  submitDisabled,
  setField,
  setVariantField,
  setImages,
  addVariant,
  removeVariant,
}) {
  const handleCategoryChange = useCallback(
    (value) => setField("category", value),
    [setField],
  );

  const handleSubcategoryChange = useCallback(
    (value) => setField("subcategory", value),
    [setField],
  );

  return (
    <>
      <ReadyMadeCatalogSection
        shopKey={shopKey}
        category={form.category}
        subcategory={form.subcategory}
        disabled={submitDisabled}
        onCategoryChange={handleCategoryChange}
        onSubcategoryChange={handleSubcategoryChange}
      />

      <ReadyMadeBasicSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <ReadyMadeSellingSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
        setVariantField={setVariantField}
        addVariant={addVariant}
        removeVariant={removeVariant}
      />

      <ReadyMadeDetailsSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <ReadyMadeImagesSection
        images={form.images}
        disabled={submitDisabled}
        setImages={setImages}
      />

      <ReadyMadeMadeToMeasureSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <ReadyMadeFlagsSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />
    </>
  );
});

export default ReadyMadeFormContent;
